// @LTG-ID: F-007
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 08_FEATURES.md#F-007, ADR-024, source_reference_v3_pending/Landing Page.html (v3) lines 1837-1945
// @CHANGED: 2026-05-24 session_005 (CHG-0076, ORCH-0073) — v3 reconciliation: pulse state + prevSector ref + useEffect (ADR-024 B2, 1500ms with cleanup on unmount/re-trigger)
// @OWNER: web

import { useEffect, useRef, useState } from "react";
import {
    ARCHETYPE_QUESTIONS,
    ARCHETYPES,
    VALUE_GAP_BY_SECTOR,
} from "../../data/archetypes";
import ExternalCTA from "../../lib/external-cta";
import { useSector } from "../../lib/use-sector";
import "./owner-archetype.css";

const INITIAL_SCORES = { lifestyle: 0, legacy: 0, liquidity: 0, stuck: 0 };

// ADR-024 B2 — value-gap card pulse duration on sector change (do not "round")
const VALUE_GAP_PULSE_DURATION_MS = 1500;

export function OwnerArchetype() {
    const { sector } = useSector();
    const [step, setStep] = useState(-1);
    const [scores, setScores] = useState(INITIAL_SCORES);
    const [pulse, setPulse] = useState(false);
    const prevSector = useRef(sector);

    // ADR-024 B2 — pulse value-gap card teal for 1500ms on sector change.
    // Cleanup clears the timer on unmount AND on re-trigger (returned function
    // runs before the next effect body) so a rapid sector flip doesn't leave a
    // stale timer firing setPulse(false) after the new pulse has started.
    // When sector goes back to null (clear), we drop the pulse immediately.
    useEffect(() => {
        if (sector === prevSector.current) return undefined;
        prevSector.current = sector;
        if (!sector) {
            setPulse(false);
            return undefined;
        }
        setPulse(true);
        const t = window.setTimeout(
            () => setPulse(false),
            VALUE_GAP_PULSE_DURATION_MS
        );
        return () => window.clearTimeout(t);
    }, [sector]);

    function pick(opt) {
        const next = { ...scores };
        Object.keys(opt.w).forEach((k) => {
            next[k] = (next[k] || 0) + opt.w[k];
        });
        setScores(next);
        setStep(step + 1);
    }

    function reset() {
        setStep(-1);
        setScores(INITIAL_SCORES);
    }

    const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const topKey = sortedScores[0][0];
    const isResult = step >= ARCHETYPE_QUESTIONS.length;
    const archetype = isResult ? ARCHETYPES[topKey] : null;

    const gap = VALUE_GAP_BY_SECTOR[sector] || VALUE_GAP_BY_SECTOR.default;
    const ownerGuessPct = (gap.ownerGuess / gap.top) * 100;
    const marketPct = (gap.marketActual / gap.top) * 100;

    return (
        <section
            className="ltg-block ltg-archetype-block"
            id="archetype"
            data-testid="owner-archetype"
        >
            <div className="ltg-container">
                <div className="ltg-block-head">
                    <span className="ltg-eyebrow">
                        Why this matters for you
                    </span>
                    <h2>
                        You already have a number in your head<em>.</em>{" "}
                        Let's see what the market actually thinks.
                    </h2>
                </div>

                <p className="ltg-archetype__lede">
                    The Strategic Snapshot is a strategic assessment, not a
                    formal appraisal. That distinction works in your favor. A
                    formal appraisal under SSVS No.&nbsp;1 or USPAP costs
                    $7,500 to $30,000 and exists to satisfy the IRS, a court,
                    or an ESOP trustee. <strong>You</strong> are not the IRS.
                    You are an owner who needs to measure where your business
                    stands, understand what moves the number, and improve it
                    on a runway you control. That is what this is built for.
                </p>

                <div className="ltg-value-gap-grid">
                    <div
                        className={`ltg-value-gap-card ${pulse ? "pulsing" : ""}`}
                        data-testid="value-gap-card"
                    >
                        <span className="ltg-value-gap__label">
                            The gap most owners discover late
                        </span>
                        <h3 className="ltg-value-gap__headline">
                            Where your number probably sits versus where the
                            market puts it.
                        </h3>
                        <svg
                            viewBox="0 0 480 280"
                            className="ltg-value-gap__chart"
                            xmlns="http://www.w3.org/2000/svg"
                            data-testid="value-gap-chart"
                        >
                            <line
                                x1="40"
                                y1="240"
                                x2="460"
                                y2="240"
                                stroke="#d8d3c6"
                            />
                            <text
                                x="240"
                                y="30"
                                fontFamily="Geist Mono, monospace"
                                fontSize="11"
                                fill="#6a7a92"
                                letterSpacing="2"
                                textAnchor="middle"
                            >
                                SECTOR {(sector || "default").toUpperCase()}
                            </text>

                            <rect
                                x="80"
                                y={240 - ownerGuessPct * 1.7}
                                width="120"
                                height={ownerGuessPct * 1.7}
                                fill="#B08D3E"
                                opacity="0.85"
                            />
                            <text
                                x="140"
                                y={240 - ownerGuessPct * 1.7 - 12}
                                fontFamily="Instrument Serif, serif"
                                fontSize="22"
                                fill="#0A2A5E"
                                textAnchor="middle"
                            >
                                {gap.ownerGuess.toFixed(1)}x
                            </text>
                            <text
                                x="140"
                                y="260"
                                fontFamily="Geist Mono, monospace"
                                fontSize="10"
                                fill="#6a7a92"
                                textAnchor="middle"
                                letterSpacing="1"
                            >
                                OWNER ASSUMED
                            </text>

                            <rect
                                x="220"
                                y={240 - marketPct * 1.7}
                                width="120"
                                height={marketPct * 1.7}
                                fill="#0A2A5E"
                            />
                            <text
                                x="280"
                                y={240 - marketPct * 1.7 - 12}
                                fontFamily="Instrument Serif, serif"
                                fontSize="22"
                                fill="#0A2A5E"
                                textAnchor="middle"
                            >
                                {gap.marketActual.toFixed(1)}x
                            </text>
                            <text
                                x="280"
                                y="260"
                                fontFamily="Geist Mono, monospace"
                                fontSize="10"
                                fill="#6a7a92"
                                textAnchor="middle"
                                letterSpacing="1"
                            >
                                MARKET MEDIAN
                            </text>

                            <rect
                                x="360"
                                y={240 - 100 * 1.7}
                                width="60"
                                height={100 * 1.7}
                                fill="#18B0BD"
                                opacity="0.6"
                            />
                            <text
                                x="390"
                                y={240 - 100 * 1.7 - 12}
                                fontFamily="Instrument Serif, serif"
                                fontSize="22"
                                fill="#0A2A5E"
                                textAnchor="middle"
                            >
                                {gap.top.toFixed(1)}x
                            </text>
                            <text
                                x="390"
                                y="260"
                                fontFamily="Geist Mono, monospace"
                                fontSize="10"
                                fill="#6a7a92"
                                textAnchor="middle"
                                letterSpacing="1"
                            >
                                TOP QUARTILE
                            </text>

                            <line
                                x1="140"
                                y1={240 - ownerGuessPct * 1.7}
                                x2="280"
                                y2={240 - marketPct * 1.7}
                                stroke="#B08D3E"
                                strokeWidth="1"
                                strokeDasharray="4,3"
                            />
                            <text
                                x="210"
                                y={
                                    (240 -
                                        ownerGuessPct * 1.7 +
                                        240 -
                                        marketPct * 1.7) /
                                        2 -
                                    8
                                }
                                fontFamily="Instrument Serif, serif"
                                fontSize="14"
                                fontStyle="italic"
                                fill="#B08D3E"
                                textAnchor="middle"
                            >
                                the gap
                            </text>
                        </svg>
                        <p className="ltg-value-gap__caption">
                            Across{" "}
                            {gap.basis === "EBITDA multiple"
                                ? "EBITDA"
                                : "revenue"}{" "}
                            multiples in this sector, owners typically assume
                            their business sits one to two turns above the
                            market median. On a $5M EBITDA business, that's{" "}
                            <strong>
                                ${((gap.ownerGuess - gap.marketActual) * 5).toFixed(1)}M
                            </strong>{" "}
                            of expected proceeds that never materialize. The
                            Snapshot tells you exactly where you sit on this
                            chart, in writing, signed by the firm.
                        </p>
                    </div>

                    <div
                        className="ltg-archetype-card"
                        data-testid="archetype-card"
                    >
                        {step === -1 && (
                            <>
                                <span className="ltg-archetype__ar-label">
                                    Four questions &middot; ninety seconds
                                </span>
                                <h3 className="ltg-archetype__intro-h">
                                    Which kind of owner are you, really?
                                </h3>
                                <p className="ltg-archetype__intro">
                                    Most owner-readiness frameworks ask about
                                    financials. This one asks about{" "}
                                    <em>you</em>. Your answer determines which
                                    Snapshot use case fits, what to do with the
                                    number, and whether a 24-month plan even
                                    makes sense.
                                </p>
                                <button
                                    className="ltg-btn ltg-btn-primary ltg-archetype__start"
                                    onClick={() => setStep(0)}
                                    data-testid="archetype-start"
                                >
                                    Start the assessment{" "}
                                    <span
                                        className="ltg-arrow"
                                        aria-hidden="true"
                                    >
                                        &rarr;
                                    </span>
                                </button>
                                <p className="ltg-archetype__note">
                                    Anonymous. No email collected. Click each
                                    answer to advance.
                                </p>
                            </>
                        )}
                        {step >= 0 && step < ARCHETYPE_QUESTIONS.length && (
                            <>
                                <div
                                    className="ltg-archetype__progress"
                                    data-testid="archetype-progress"
                                >
                                    {ARCHETYPE_QUESTIONS.map((_, i) => {
                                        let cls = "";
                                        if (i < step) cls = "is-done";
                                        else if (i === step) cls = "is-active";
                                        return (
                                            <span
                                                key={`progress-${i}`}
                                                className={cls}
                                            />
                                        );
                                    })}
                                </div>
                                <span className="ltg-archetype__q-num">
                                    Question {step + 1} of{" "}
                                    {ARCHETYPE_QUESTIONS.length}
                                </span>
                                <h3
                                    className="ltg-archetype__q"
                                    data-testid="archetype-q"
                                >
                                    {ARCHETYPE_QUESTIONS[step].q}
                                </h3>
                                <div className="ltg-archetype__options">
                                    {ARCHETYPE_QUESTIONS[step].opts.map(
                                        (o, i) => (
                                            <button
                                                key={`opt-${step}-${i}`}
                                                className="ltg-archetype__opt"
                                                onClick={() => pick(o)}
                                                data-testid={`archetype-opt-${step}-${i}`}
                                            >
                                                {o.text}
                                            </button>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                        {archetype && (
                            <div
                                className="ltg-archetype__result"
                                style={{ borderTopColor: archetype.color }}
                                data-testid="archetype-result"
                                data-archetype={topKey}
                            >
                                <span className="ltg-archetype__result-label">
                                    Your archetype
                                </span>
                                <h3
                                    className="ltg-archetype__result-name"
                                    style={{ color: archetype.color }}
                                    data-testid="archetype-result-name"
                                >
                                    {archetype.label}
                                    <span
                                        className="ltg-bubble-period"
                                        style={{
                                            "--bp-color": archetype.color,
                                        }}
                                        aria-hidden="true"
                                    />
                                </h3>
                                <p className="ltg-archetype__result-headline">
                                    {archetype.headline}
                                </p>
                                <p className="ltg-archetype__result-body">
                                    {archetype.body}
                                </p>
                                <div className="ltg-archetype__result-cta">
                                    <ExternalCTA
                                        ctaId={`archetype-result-${topKey}`}
                                        className="ltg-btn ltg-archetype__cta-btn"
                                    >
                                        Get Your Valuation{" "}
                                        <span
                                            className="ltg-arrow"
                                            aria-hidden="true"
                                        >
                                            &rarr;
                                        </span>
                                    </ExternalCTA>
                                    <button
                                        type="button"
                                        className="ltg-btn ltg-archetype__retake"
                                        onClick={reset}
                                        data-testid="archetype-retake"
                                    >
                                        Retake
                                    </button>
                                </div>
                                <p className="ltg-archetype__result-disc">
                                    Indicative valuation. Not a formal
                                    appraisal under SSVS No.&nbsp;1.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default OwnerArchetype;
